'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useId, useRef, useState } from 'react'
import { Controller, ControllerRenderProps, FieldValues } from 'react-hook-form'
import Select, { components, InputActionMeta, Props as SelectProps } from 'react-select'
import { AsyncPaginate, withAsyncPaginate } from 'react-select-async-paginate'
import Creatable from 'react-select/creatable'

import { TCommonFields } from '../../types'
import { FieldRow, getFieldRules } from '../field-row'
import { useFieldState } from '../hooks'
import { GetValueByPath } from '../../../utils'

type TSelectOption = {
    children: React.ReactNode
    innerProps: Record<string, any>
}

export type SelectOptionType = {
    value: number | string
    label: React.ReactNode
}

export type TSelectField<T> = TCommonFields<T> & {
    type: 'select'
    options?: readonly SelectOptionType[]
    /** react-select props; the value binding stays with the form */
    props?: Omit<SelectProps, 'value' | 'onChange' | 'onBlur' | 'name'>
    default?: SelectOptionType | ((row: any) => SelectOptionType | SelectOptionType['value'] | undefined) | string | number
    loadDefault?: () => Promise<SelectOptionType["value"]> | Promise<(SelectOptionType["value"])[]>
    creatable?: boolean
    loadOptions?: (
        inputValue: string,
        page: number,
        pageSize: number
    ) => Promise<{ data: SelectOptionType[]; total: number }>
    generateKey?: (value: any) => (React.Key | undefined)
    generateKeyComponent?: () => (React.Key | undefined)
    onDefaultNotFound?: (value: number | string) => Promise<SelectOptionType | undefined>
    extractFieldValue?: (data: any) => any
}

const isOption = (value: unknown): value is SelectOptionType =>
    value != null && typeof value === 'object' && !Array.isArray(value) && 'value' in value

/** The form stores option values; a `default` may also be given as a whole option. */
export const toOptionValue = (value: unknown) => (isOption(value) ? value.value : value)

const SelectOption = ({ children, ...props }: TSelectOption) => {
    const { onMouseMove, onMouseOver, ...rest } = props.innerProps
    const newProps: any = { ...props, innerProps: rest }

    return (
        <components.Option {...newProps} className="react-select-option">
            {children}
        </components.Option>
    )
}

type TSelectFieldProps<T> = {
    field: ControllerRenderProps<any, string>
    field_: TSelectField<T>
    disabled: boolean
}

const AsyncPaginateCreateableSelect = withAsyncPaginate(Creatable)

const SelectField = <T,>({
    field: { ref, value, onChange, ...rest },
    field_,
    disabled,
}: TSelectFieldProps<T>) => {
    const pageSize = 50
    const [loading, setLoading] = useState(false)
    const [input, setInput] = useState('')
    const [loadedOptions, setLoadedOptions] = useState<SelectOptionType[]>([])
    const t = useTranslations('form.select-field')

    const allOptions = [
        ...(field_.options ?? []),
        ...loadedOptions
    ]

    const onSelectChange = (newValue: unknown) => {
        const getValue = () => {
            if (newValue == null)
                return null

            if (Array.isArray(newValue))
                return newValue.map(val => val.value)

            return (newValue as SelectOptionType).value
        }

        const value_ = getValue()

        field_.onChange && field_.onChange(value_ == null ? undefined : value_)
        onChange(value_ == null ? undefined : value_)
    }

    const convertedValue = (() => {
        const values = Array.isArray(value) ? value : (value != null ? [value] : [])
        const result: SelectOptionType[] = []

        for (const val of values) {
            const key = toOptionValue(val)
            const optValue = allOptions.find(opt => opt.value == key)
            if (optValue) {
                result.push(optValue)
            }
            else if (isOption(val)) {
                result.push(val)
            }
            else if (field_.creatable && key != null) {
                result.push({ label: String(key), value: key as SelectOptionType['value'] })
            }
        }

        if (Array.isArray(value))
            return result

        return result[0] || (field_.props?.isMulti ? [] : undefined)
    })()

    // Resolve missing labels for initial values
    useEffect(() => {
        if (!field_.onDefaultNotFound || value == null)
            return

        const values = Array.isArray(value) ? value : [value]
        const missingValues = values.filter(val => !allOptions.some(opt => opt.value == val))

        if (missingValues.length === 0)
            return

        const resolveMissing = async () => {
            const newResolved: SelectOptionType[] = []
            for (const val of missingValues) {
                const fetched = await field_.onDefaultNotFound!(val)
                if (fetched)
                    newResolved.push(fetched)
            }

            if (newResolved.length > 0)
                setLoadedOptions(prev => [...prev, ...newResolved])
        }

        resolveMissing()
    }, [value, field_.onDefaultNotFound, allOptions.length])

    const onInputChange = (value: string, action: InputActionMeta) => {
        switch (action.action) {
            case 'input-change':
            case 'menu-close':
                setInput(value)
                return value
            case 'set-value':
                setInput('')
                return ''
            default:
                return input
        }
    }

    const props: SelectProps = {
        instanceId: useId(),
        inputId: rest.name,
        ...rest,
        isDisabled: disabled,
        noOptionsMessage: () => t('no-options'),
        placeholder: t('select'),
        className: 'form-select',
        classNamePrefix: 'react-select',
        components: { Option: SelectOption },
        isClearable: field_.props?.isClearable,
        isSearchable: field_.props?.isSearchable,
        menuPlacement: 'auto',
        menuPortalTarget: typeof document !== 'undefined' ? document?.body : undefined,
        menuPosition: 'fixed',
        styles: {
            menuPortal: base => ({
                ...base,
                zIndex: 9999,
                pointerEvents: 'all',
            }),
            menu: base => ({
                ...base,
                backgroundColor: 'var(--gray-1)',
                border: '1px solid var(--gray-3)',
                boxShadow: 'var(--shadow-2)',
            }),
            option: (base, state) => ({
                ...base,
                color: 'var(--gray-11)',
                backgroundColor: state.isSelected
                    ? 'var(--blue-a4)'
                    : state.isFocused
                        ? 'var(--blue-a2)'
                        : 'var(--color-panel-solid)',
                ':hover': {
                    backgroundColor: 'var(--blue-a2)',
                    cursor: 'pointer',
                },
            }),
        },
        menuShouldScrollIntoView: false,
        closeMenuOnSelect: !(field_.props?.isMulti ?? false),
        ...(field_.creatable ? { formatCreateLabel: (val: string) => t('create', { value: val }) } : {}),
        ...field_.props,
        // the form owns the value even when `props` (typed loosely before) carries one
        name: rest.name,
        onBlur: rest.onBlur,
        value: convertedValue,
        onChange: onSelectChange,
        onInputChange,
        inputValue: input,
    }

    const loadOptions = async (
        inputValue: string,
        _: any,
        additional?: { page: number }
    ) => {
        const page = additional?.page ?? 0
        setLoading(true)

        try {
            const { data, total } = await field_.loadOptions!(inputValue, page, pageSize)

            // Update loaded options for label lookup
            setLoadedOptions(prev => {
                const newOptions = data.filter(opt => !prev.some(p => p.value === opt.value))
                return [...prev, ...newOptions]
            })

            return {
                options: data,
                hasMore: (page + 1) * pageSize < total,
                additional: { page: page + 1 },
            }
        }
        finally {
            setLoading(false)
        }
    }

    const selectRef = useRef(null)

    if (field_.loadOptions) {
        const SelectComponent = field_.creatable ? AsyncPaginateCreateableSelect : AsyncPaginate
        return (
            <SelectComponent
                {...props}
                selectRef={selectRef}
                defaultOptions
                loadOptions={loadOptions}
                isLoading={loading}
                additional={{ page: 0 }}
            />
        )
    }

    const SelectComponent = field_.creatable ? Creatable : Select
    return <SelectComponent {...props} ref={ref} options={field_.options ?? []} />
}

type TProps<T> = {
    rowField: TSelectField<T>
    fieldName: string
    labelWidth?: string
}

export const getSelectDefaultValue = async <T,>(
    fieldName: string,
    field: TSelectField<T>,
    data: FieldValues
) => {
    const fieldItem = GetValueByPath(data, fieldName)
    // 0 and '' are real values
    if (fieldItem != null)
        return field.extractFieldValue ? field.extractFieldValue(fieldItem) : fieldItem

    if (field.loadDefault)
        return await field.loadDefault()

    return toOptionValue(typeof field.default === 'function' ? field.default(data) : field.default)
}

const isEmptyValue = (value: unknown) =>
    value == null || (Array.isArray(value) && value.length == 0)

const SelectFieldDefault = <T,>({ rowField, fieldName, labelWidth }: TProps<T>) => {
    const t = useTranslations('form.select-field')
    const { disabled, required } = useFieldState(rowField)

    const requiredRule = rowField.validation?.required
    const customMessage = typeof requiredRule === 'object' ? requiredRule?.message : requiredRule
    const requiredMessage = typeof customMessage === 'string' && customMessage != ''
        ? customMessage
        : t('is-required', { label: rowField.label })

    return (
        <FieldRow
            fieldName={fieldName}
            label={rowField.label}
            required={required}
            labelWidth={labelWidth}
            className="form-select-item"
        >
            <Controller
                name={fieldName}
                // `required` goes through validate so an empty array counts as missing too
                rules={getFieldRules(
                    rowField,
                    false,
                    required ? { required: value => !isEmptyValue(value) || requiredMessage } : undefined
                )}
                // only reached when the field is rendered outside of `Form` (its defaults come from generateDefaultValues)
                defaultValue={typeof rowField.default === 'function' ? undefined : toOptionValue(rowField.default)}
                render={({ field }) => (
                    <SelectField
                        field={field}
                        field_={rowField}
                        disabled={disabled}
                        key={rowField.generateKeyComponent?.()}
                    />
                )}
            />
        </FieldRow>
    )
}

export default SelectFieldDefault
